/**
 * Zustand store for the global time-window. Just one field —
 * keep the surface tiny so consumers can subscribe with the
 * narrowest possible selector and avoid spurious re-renders.
 *
 * Initial value resolution order:
 *   1. localStorage `verkh.timeWindow` — the user's saved default.
 *   2. Default `30d` per the design.
 *
 * URL params are NOT consulted here; that's the URL-sync hook's
 * job, called once near the app root. Decoupling means unit tests
 * can drive the store without mocking react-router.
 */

import { create } from "zustand";
import {
  isTimeWindow,
  type TimeWindow,
} from "./types";

const STORAGE_KEY = "verkh.timeWindow";
const DEFAULT_WINDOW: TimeWindow = "30d";

interface TimeWindowState {
  window: TimeWindow;
  /**
   * Set the window AND persist to localStorage. The URL is not
   * updated here — the URL-sync hook listens to state changes and
   * mirrors them into the URL.
   */
  setWindow: (window: TimeWindow) => void;
  /**
   * Set the window WITHOUT persisting to localStorage — used by the
   * URL-sync hook to apply an incoming URL value without making it
   * the user's default. A user who follows a link with `?window=24h`
   * shouldn't have their saved default changed under them.
   */
  setWindowFromUrl: (window: TimeWindow) => void;
}

function readInitialWindow(): TimeWindow {
  if (typeof window === "undefined") return DEFAULT_WINDOW;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (isTimeWindow(saved)) return saved;
  } catch {
    // localStorage unavailable (private mode, SSR) — fall through.
  }
  return DEFAULT_WINDOW;
}

export const useTimeWindowStore = create<TimeWindowState>((set) => ({
  window: readInitialWindow(),
  setWindow: (next) => {
    set({ window: next });
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // localStorage write failed (quota, private mode) — the
        // in-memory state is still authoritative for this session.
      }
    }
  },
  setWindowFromUrl: (next) => {
    set({ window: next });
  },
}));
